import { Link } from 'react-router-dom'
import { EmptyState, Prose } from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import './LegalPage.css'

const TELEPHONE = '06 79 33 33 64'
const TEL_HREF = 'tel:+33679333364'
const EMAIL = 'contact-vaux@azoya-spa.com'
const ADRESSE = '1bis, Rue Saint-Vincent 89290 Auxerre'

// Affiché quand l'API n'a pas répondu et qu'aucune version n'avait été
// mémorisée : la page existe, c'est son corps qui manque. Le bandeau et le
// titre restent donc en place au-dessus.
function ContenuIndisponible({ onReessayer }) {
  return (
    <div className="LegalPage-indispo">
      <EmptyState
        title="Contenu momentanément indisponible."
        actionLabel="Réessayer"
        onAction={onReessayer}
      >
        <p>
          Le texte de cette page existe et n’a pas changé : c’est son chargement qui a échoué.
          Réessayez dans un instant — ou demandez-nous directement l’information dont vous avez
          besoin, on vous répond.
        </p>
      </EmptyState>

      <div className="LegalPage-carte">
        <div className="LegalPage-surtitre">En attendant</div>
        <Prose>
          <p>
            Par téléphone : <a href={TEL_HREF}>{TELEPHONE}</a>
            <br />
            Par email : <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            <br />
            Par courrier : {ADRESSE}
          </p>
        </Prose>
      </div>
    </div>
  )
}

function Sommaire({ entrees }) {
  return (
    <nav className="LegalPage-toc-liste" aria-label="Les articles">
      {entrees.map((entree) => (
        <a key={entree.id} href={`#${entree.id}`}>
          <span className="LegalPage-toc-num">{entree.num}</span>
          <span className="LegalPage-toc-label">{entree.label}</span>
        </a>
      ))}
    </nav>
  )
}

/**
 * Le gabarit des deux pages légales. Il porte le bandeau, le sommaire et le
 * renvoi de bas de page ; le corps lui est passé en `children`, déjà enveloppé
 * dans un ou plusieurs `Prose` par la page appelante — les mentions légales
 * intercalent leur panneau « transparence IA » entre deux blocs de prose.
 *
 * `sommaire` est calculé par la page à partir des titres que `Prose` lui
 * remonte : le gabarit ne connaît pas le contenu, qui vient du back-office.
 */
export default function LegalPage({
  title,
  lead,
  meta,
  sibling,
  sommaire = [],
  indisponible = false,
  onReessayer,
  children,
}) {
  const avecSommaire = !indisponible && sommaire.length > 0

  return (
    <div className="LegalPage">
      <div className="LegalPage-bandeau-plein">
        <div className="LegalPage-bandeau">
          <div className="LegalPage-bandeau-inner">
            <div className="LegalPage-pastille">
              <span aria-hidden="true">✦</span> Informations légales
            </div>
            <h1>{title}</h1>
            {lead && <p className="LegalPage-lead">{lead}</p>}
            {meta && <p className="LegalPage-meta">{meta}</p>}
          </div>
        </div>
      </div>

      <div className="LegalPage-shell" data-avec-sommaire={avecSommaire ? 'oui' : 'non'}>
        {avecSommaire && (
          <aside className="LegalPage-toc-aside">
            <div className="LegalPage-surtitre">Les articles</div>
            <Sommaire entrees={sommaire} />
          </aside>
        )}

        <div className="LegalPage-col">
          {avecSommaire && (
            <details className="LegalPage-toc-details">
              <summary>
                Aller à un article <span>({sommaire.length})</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e86f51" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </summary>
              <Sommaire entrees={sommaire} />
            </details>
          )}

          {indisponible ? <ContenuIndisponible onReessayer={onReessayer} /> : children}

          {sibling && (
            <div className="LegalPage-voir-aussi">
              <span className="LegalPage-surtitre">Voir aussi</span>
              <Link to={sibling.href}>{sibling.label}</Link>
              <a href={TEL_HREF}>Une question ? {TELEPHONE}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
